/**
 * `ProjectModelLibrarySync.ts` — a placed model and its library entry have
 * drifted apart: detecting it in EITHER direction, and pulling the library's
 * changes down when the user accepts.
 *
 * **Web-first. There is no Unity counterpart** — the desktop client never
 * notices, never offers, and never auto-pulls. (Its one library→project apply,
 * `ModifyAssetUsingPartDataAsync`, has a single `true` caller,
 * `AssetFactory.CreateAsync` — the place-a-NEW-instance path. Scene load goes
 * through `ImportAsset` and applies the snapshot's own values.) Do not
 * "restore parity" by making this automatic; the whole point is that the user
 * decides.
 *
 * ## What an update can and cannot contain
 *
 * Save to Library never rewrites the GLB: `copyAssetBinary` runs only on the
 * fork branch, which mints a NEW asset id this project has never heard of. The
 * in-place branch rewrites the PART rows, mints fresh library material rows,
 * and bumps `AssetVersion`. So the entire payload an update can ever carry is
 * the bake surface — per-part transforms (never the root's), visibility, names,
 * aliases and materials. **Geometry can never change under a placed model**;
 * say so before anyone builds a re-download.
 *
 * ## Which way it drifted is the watermark's job; THAT it drifted is the values'
 *
 * The value comparison (`countChanges`) is symmetric — it says the placement
 * and the library entry disagree, not who moved. `AssetVersion` is what breaks
 * the tie, and it is the only thing that can:
 *
 *  - `syncedVersion < libraryVersion` → the library was re-saved since this
 *    placement last looked. **Pull.** (The user may ALSO have edited locally;
 *    that is folded in here rather than reported separately, because the review
 *    sheet lets them choose what to adopt part by part.)
 *  - `syncedVersion >= libraryVersion` and the values still differ → nobody
 *    re-saved the library, so the difference is this project's. **Push.**
 *
 * Neither direction is announced without a watermark: absent means UNKNOWN
 * (a placement written before this feature), which the back-fill resolves.
 *
 * ## Why the PULL trigger is a stored watermark and not a content diff
 *
 * Every one of those baked fields is also one the user edits locally — they are
 * all on `EntitySnapshotPatch`. So "the library's value differs from mine"
 * cannot mean "the library changed"; it usually means "I changed it here". The
 * alias feature escaped this by comparing DIRECTIONALLY (only entries the
 * library has and the project lacks), which works for a set of names and has no
 * analogue for a transform or a colour.
 *
 * The only monotonic "the library entry was re-saved" marker is
 * `AssetData.AssetVersion`, incremented by exactly one writer
 * (`ProjectModelSaveToLibrary`). Each placement records the version it is
 * synced to on its ROOT snapshot (`LibrarySyncedVersion`), and the comparison
 * is `libraryVersion > syncedVersion`. `LastModified` was rejected: Unity moves
 * it from a background variant-processing poll with no user action at all.
 *
 * ## The pull must not re-share material rows
 *
 * Material rows are customer-scoped, so `snapshot.materialIds := part.materialIds`
 * — what `resetSceneLibraryMaterialsAsync` does — would hand this project's
 * snapshots back to the library's rows and re-open the cross-project leak that
 * per-placement copies closed. The pull copies the library's material VALUES
 * onto the ids this placement already owns, and forks first if a legacy
 * placement is still on a library-owned id.
 */

import { mergeLibraryAliases, type PartAlias } from "@models/data/aliasData";
import type {
  EntityData,
  EntitySnapshot,
  EntitySnapshotPatch,
} from "@models/data/entityData";

/** `EntitySnapshotPatch` is all-readonly; the builder below fills one in place. */
type Mutable<T> = { -readonly [K in keyof T]: T[K] };
import type { MaterialDto } from "@models/data/materialDto";
import { type PartDto, partMaterialIds, partTransform } from "@models/data/partDto";
import { vec3From } from "@models/data/nameFloatMap";
import { readAliases } from "@models/data/aliasData";
import {
  buildLibraryDiff,
  EMPTY_LIBRARY_DIFF,
  libraryDiffKey,
  NOTHING_EXCLUDED,
  type LibraryDiff,
  type LibraryDiffDirection,
  type LibraryDiffGroup,
  type LibrarySyncExclusions,
} from "./libraryDiff";
import type { IStorageModel } from "@models/storage/IStorageModel";
import type { ProjectModel } from "./ProjectModel";

/**
 * Which way a drift runs.
 *
 * `pull` — the library entry moved ahead; accepting adopts it.
 * `push` — this project holds changes the library entry does not; accepting
 * publishes them (which is Save to Library, so the push half is carried out by
 * `ProjectModelSaveToLibrary` rather than here).
 */
export type ModelSyncDirection = "pull" | "push";

/** One placement of a drifted asset. */
export interface ModelSyncPlacement {
  readonly sceneId: string;
  readonly entityId: string;
  readonly rootSnapshotId: string;
  /** Root snapshot's display name — what the user calls this placement. */
  readonly label: string;
  /** The version THIS placement is synced to. */
  readonly syncedVersion: number;
  /**
   * Per placement, unlike the asset-level copy. For a PULL every placement of
   * one asset compares against the same library rows and would report the same
   * numbers; for a PUSH the changes are this project's own edits, so two
   * placements of one asset genuinely differ.
   */
  readonly changes: ModelSyncChanges;
}

/**
 * What accepting would change, counted in PARTS.
 *
 * Deliberately framed as "what this would do to your project", not "what the
 * library author edited" — those differ whenever the user has also edited
 * locally, and there is no per-field provenance to separate them. The honest
 * number is the one that tells you what you are about to overwrite.
 */
export interface ModelSyncChanges {
  /** Parts that differ in at least one field. */
  readonly parts: number;
  readonly renamed: number;
  readonly moved: number;
  readonly visibility: number;
  readonly materials: number;
  readonly aliases: number;
}

/** One library asset that has drifted from at least one of its placements. */
export interface ModelSyncAsset {
  readonly assetId: string;
  /** Display name of the first placement — models are named per placement. */
  readonly label: string;
  readonly direction: ModelSyncDirection;
  readonly libraryVersion: number;
  /** Lowest synced version across the placements — what the copy shows. */
  readonly syncedVersion: number;
  readonly placements: ReadonlyArray<ModelSyncPlacement>;
  /** The first placement's counts — see {@link ModelSyncPlacement.changes}. */
  readonly changes: ModelSyncChanges;
}

/** Everything in the loaded scenes that disagrees with its library entry. */
export interface ModelSyncReport {
  readonly projectId: string;
  /**
   * Scenes covered. The signature describes the LIBRARY side only, so two
   * scenes holding placements of the same drifted asset hash identically — a
   * consumer de-duplicating on the signature alone would drop the second and
   * leave it stale. Same trap the alias report documents.
   */
  readonly sceneIds: ReadonlyArray<string>;
  readonly assets: ReadonlyArray<ModelSyncAsset>;
  /** Parts that would change across every drifted asset — the badge count. */
  readonly totalParts: number;
  /**
   * `assetId=version` per drifted asset, sorted and joined. Dismissal is
   * remembered against these, so declining silences exactly this library state
   * while the NEXT save bumps the version and legitimately asks again.
   */
  readonly signature: string;
}

export interface ModelSyncResult {
  /** Placements successfully brought up to date. */
  readonly applied: number;
  /** Placements that failed; failures are counted, never thrown. */
  readonly failed: number;
}

export class ProjectModelLibrarySync {
  /**
   * Compare each loaded scene's placements against their library entries.
   * Pure and in-memory — the versions were fetched with the part rows.
   *
   * Returns `null` when nothing is behind, so a caller can treat "no report"
   * as "nothing to say" without inspecting counts.
   */
  getLibraryModelSyncReport(
    this: ProjectModel,
    sceneId?: string,
  ): ModelSyncReport | null {
    const projectId = this.activeProjectId;
    if (!projectId) return null;
    const state = internals(this);

    const scenes = sceneId ? [sceneId] : [...state.m_entitiesByScene.keys()];
    // Keyed on asset AND direction: one asset cannot drift both ways at once
    // (the watermark decides), but two placements of it can — one behind the
    // library while another carries local edits.
    const groups = new Map<string, {
      assetId: string;
      direction: ModelSyncDirection;
      libraryVersion: number;
      placements: ModelSyncPlacement[];
    }>();
    const coveredScenes: string[] = [];

    for (const id of scenes) {
      let sceneCounted = false;
      for (const entity of this.getEntities(id)) {
        const assetId = entity.assetId;
        if (!assetId) continue;
        const libraryVersion = state.m_assetVersionById.get(assetId);
        // `<= 0` is "never saved to the library" — the same gate the scene
        // reset applies. Such an asset has no library entry to drift from, so
        // a freshly imported model never nags to be published.
        if (typeof libraryVersion !== "number" || libraryVersion <= 0) continue;

        const root = rootSnapshotOf(entity);
        if (!root) continue;
        const synced = root.librarySyncedVersion;
        // Absent = unknown, NOT zero. Back-filled elsewhere; never offered.
        if (typeof synced !== "number") continue;

        const direction: ModelSyncDirection = synced < libraryVersion ? "pull" : "push";
        const placement: ModelSyncPlacement = {
          sceneId: id,
          entityId: entity.id,
          rootSnapshotId: root.id,
          label: root.displayName || root.name || "Model",
          syncedVersion: synced,
          changes: countChanges(this, id, entity.id, direction),
        };
        // A pull is announced by the VERSION — the library moved, and that is
        // news even before the values are counted (the material rows may not
        // have landed yet). A push has no version to announce it, so it exists
        // only as long as something actually differs.
        if (direction === "push" && placement.changes.parts === 0) continue;

        const key = `${assetId}|${direction}`;
        const group = groups.get(key)
          ?? { assetId, direction, libraryVersion, placements: [] };
        group.placements.push(placement);
        groups.set(key, group);
        if (!sceneCounted) {
          coveredScenes.push(id);
          sceneCounted = true;
        }
      }
    }

    if (groups.size === 0) return null;

    const assets: ModelSyncAsset[] = [...groups.values()]
      .map((g) => ({
        assetId: g.assetId,
        label: g.placements[0].label,
        direction: g.direction,
        libraryVersion: g.libraryVersion,
        syncedVersion: Math.min(...g.placements.map((p) => p.syncedVersion)),
        placements: g.placements,
        changes: g.placements[0].changes,
      }))
      // Pull first: the library moving is news, a local edit is something the
      // user did on purpose.
      .sort((a, b) =>
        a.direction === b.direction
          ? a.label.localeCompare(b.label)
          : a.direction === "pull" ? -1 : 1,
      );

    return {
      projectId,
      sceneIds: [...coveredScenes].sort(),
      assets,
      totalParts: assets.reduce((sum, a) => sum + a.changes.parts, 0),
      // A PULL is silenced per library version — the next save bumps it and
      // legitimately asks again. A PUSH has no library version of its own to
      // move, so its fingerprint is the SIZE of what is unpublished: editing
      // further parts asks again, editing and reverting does not.
      signature: assets
        .map((a) =>
          a.direction === "pull"
            ? `${a.assetId}=${a.libraryVersion}`
            : `${a.assetId}+${a.changes.parts}`,
        )
        .sort()
        .join("|"),
    };
  }

  /**
   * Stamp the current library version onto every placement that carries no
   * watermark, and announce whatever is genuinely behind.
   *
   * The back-fill is what lets the feature work for projects that already
   * exist: without it, absent would have to be read as "behind since version
   * 0" and every model in every project would light up on the first open after
   * ship. One `UpdateItem` per placement, once ever, non-fatal.
   */
  reportLibraryModelDrift(this: ProjectModel, sceneId: string): void {
    void backfillWatermarksAsync(this, sceneId)
      .catch(() => {
        /* non-fatal: retried on the next open */
      })
      // The back-fill has to land first: a placement with no watermark yet is
      // not a drift candidate in either direction, so reading its material
      // rows before then would fetch for assets that are about to be excluded.
      .then(() => prefetchLibraryMaterials(this, sceneId))
      .then(() => {
        const report = this.getLibraryModelSyncReport(sceneId);
        if (report) this.onLibraryModelsOutOfSync.emit(report);
      });
  }

  /**
   * The value-level differences between one placed model and its library
   * entry, in either direction — the list both the Save-to-Library dialog and
   * the update prompt render. Fetches fresh rows; see `libraryDiff.ts`.
   */
  async getLibraryDiffAsync(
    this: ProjectModel,
    sceneId: string,
    entityId: string,
    direction: LibraryDiffDirection,
    signal?: AbortSignal,
  ): Promise<LibraryDiff> {
    const state = internals(this);
    const ctx = state.getStorageContext();
    const entity = this.getEntityById(sceneId, entityId);
    if (!ctx || !entity?.assetId) return EMPTY_LIBRARY_DIFF;
    return buildLibraryDiff({
      storage: ctx.storage,
      customerId: ctx.customerId,
      assetId: entity.assetId,
      snapshots: entity.snapshots,
      ownMaterial: (id) => this.getMaterialData(id),
      direction,
      signal,
    });
  }

  /**
   * Pull the library's changes into every placement of `assetIds`, across the
   * scenes the report covers.
   *
   * Re-reads live state rather than trusting the report: it sits in front of
   * the user for as long as they take to answer.
   */
  async syncLibraryModelsAsync(
    this: ProjectModel,
    report: ModelSyncReport,
    assetIds: ReadonlyArray<string>,
    signal?: AbortSignal,
    excluded: LibrarySyncExclusions = NOTHING_EXCLUDED,
  ): Promise<ModelSyncResult> {
    const state = internals(this);
    const ctx = state.getStorageContext();
    if (!ctx) return { applied: 0, failed: 0 };

    const wanted = new Set(assetIds);
    let applied = 0;
    let failed = 0;

    for (const asset of report.assets) {
      if (!wanted.has(asset.assetId)) continue;
      // A push is Save to Library's job, not this one — the caller routes it
      // there. Applying it here would pull the library ONTO the local edits it
      // is meant to publish, i.e. discard them.
      if (asset.direction !== "pull") continue;

      // Fresh part rows: the retained catalogs hold transforms, materials and
      // aliases but not names or visibility, and the rows may have moved again
      // while the prompt was on screen.
      let parts: ReadonlyArray<PartDto>;
      try {
        parts = await ctx.storage.getParts(ctx.customerId, asset.assetId, signal);
      } catch (err) {
        console.warn(`[ProjectModel] library sync: getParts failed (${asset.assetId}):`, err);
        failed += asset.placements.length;
        continue;
      }
      const partById = new Map<string, PartDto>();
      for (const part of parts) {
        if (part.ID) partById.set(part.ID, part);
      }
      if (partById.size === 0) {
        failed += asset.placements.length;
        continue;
      }

      // One material read per asset, shared by every placement of it.
      const libraryMaterials = await readLibraryMaterials(ctx, parts, signal);

      for (const placement of asset.placements) {
        try {
          await applyToPlacement(
            this, ctx, placement, partById, libraryMaterials, asset, excluded, signal,
          );
          applied++;
        } catch (err) {
          console.warn(
            `[ProjectModel] library sync failed for ${placement.label} (${placement.entityId}):`,
            err,
          );
          failed++;
        }
      }
    }

    return { applied, failed };
  }
}

/**
 * Count, per part, what a pull would change on this placement.
 *
 * Every field is compared against the retained LIBRARY part row, so this is
 * in-memory and synchronous — except materials, which need the library's
 * material rows. Those are fetched lazily for drifted assets only
 * (`prefetchLibraryMaterials`); before they land, materials are simply not
 * counted rather than guessed at. A count that is briefly low is honest; an
 * invented one is not.
 *
 * Materials cannot be compared by ID: Save to Library mints a fresh library
 * material id on EVERY save whether or not the material changed, so the ids
 * always differ and would always report a change. The comparison is by VALUE.
 */
function countChanges(
  model: ProjectModel,
  sceneId: string,
  entityId: string,
  direction: ModelSyncDirection,
): ModelSyncChanges {
  const state = internals(model);
  const zero: ModelSyncChanges = {
    parts: 0, renamed: 0, moved: 0, visibility: 0, materials: 0, aliases: 0,
  };
  const entity = model.getEntityById(sceneId, entityId);
  if (!entity) return zero;

  let parts = 0, renamed = 0, moved = 0, visibility = 0, materials = 0, aliases = 0;
  for (const snapshot of walkSnapshots(entity.snapshots)) {
    const partId = snapshot.partId;
    if (!partId) continue;
    let touched = false;

    const bake = state.m_partBakeById.get(partId);
    if (bake) {
      if (
        (bake.name !== "" && bake.name !== snapshot.name)
        || (bake.displayName !== "" && bake.displayName !== snapshot.displayName)
      ) {
        renamed++;
        touched = true;
      }
      if (bake.visibility !== snapshot.visibility) {
        visibility++;
        touched = true;
      }
    }

    // The root's transform is never adopted, so it is never a change.
    if (!snapshot.isRoot) {
      const libraryPose = state.m_partTransformById.get(partId);
      if (libraryPose && !sameTransform(libraryPose, snapshot.transform)) {
        moved++;
        touched = true;
      }
    }

    // The one asymmetric field. A PULL merges, so it only counts entries the
    // library has and this project lacks — adopting can never delete a local
    // language. A PUSH replaces (`bakeSnapshotsIntoParts` writes the whole
    // list), so any difference at all is a difference.
    const libraryAliases = state.m_partAliasesById.get(partId);
    if (direction === "push") {
      if (!sameAliases(snapshot.aliases, libraryAliases ?? [])) {
        aliases++;
        touched = true;
      }
    } else if (libraryAliases && libraryAliases.length > 0) {
      const merged = mergeLibraryAliases(snapshot.aliases, libraryAliases);
      if (!sameAliases(merged, snapshot.aliases)) {
        aliases++;
        touched = true;
      }
    }

    const libIds = state.m_partMaterialIdsById.get(partId);
    if (libIds && libIds.length === snapshot.materialIds.length) {
      for (let i = 0; i < libIds.length; i++) {
        const source = state.m_libraryMaterialsById.get(libIds[i]);
        const own = state.m_materialsById.get(snapshot.materialIds[i]);
        if (!source || !own) continue; // not fetched yet — do not guess
        if (!sameMaterialValues(source, own)) {
          materials++;
          touched = true;
          break;
        }
      }
    }

    if (touched) parts++;
  }
  return { parts, renamed, moved, visibility, materials, aliases };
}

/**
 * Compare two material rows by value, ignoring the id (they are copies of one
 * another under different ids by design) and the bookkeeping attributes a
 * write stamps.
 */
function sameMaterialValues(a: MaterialDto, b: MaterialDto): boolean {
  const strip = (m: MaterialDto): string => {
    const rest: Record<string, unknown> = { ...(m as Record<string, unknown>) };
    delete rest.ID;
    delete rest.pk;
    delete rest.sk;
    delete rest.Type;
    delete rest.LastModified;
    delete rest.ModifiedTimestamp;
    return JSON.stringify(
      Object.keys(rest).sort().map((k) => [k, rest[k]]),
    );
  };
  return strip(a) === strip(b);
}

/**
 * Pull the library material rows for the drifted assets into their own cache,
 * so the change counts can include materials.
 *
 * Only for assets that ARE behind — this is one batch read on the rare path,
 * not a cost every scene load pays for a comparison it will not make.
 */
async function prefetchLibraryMaterials(model: ProjectModel, sceneId: string): Promise<void> {
  const state = internals(model);
  const ctx = state.getStorageContext();
  if (!ctx) return;

  const wanted = new Set<string>();
  for (const entity of model.getEntities(sceneId)) {
    const assetId = entity.assetId;
    if (!assetId) continue;
    const libraryVersion = state.m_assetVersionById.get(assetId);
    if (typeof libraryVersion !== "number" || libraryVersion <= 0) continue;
    // Every library-linked placement, not just the ones behind. A PUSH is
    // detected purely from the values, and an unpublished COLOUR is the common
    // case — without these rows the comparison would be blind to exactly the
    // edit the user is most likely to have made.
    const root = rootSnapshotOf(entity);
    if (typeof root?.librarySyncedVersion !== "number") continue;
    for (const snapshot of walkSnapshots(entity.snapshots)) {
      for (const id of snapshot.partId ? state.m_partMaterialIdsById.get(snapshot.partId) ?? [] : []) {
        if (id && !state.m_libraryMaterialsById.has(id)) wanted.add(id);
      }
    }
  }
  if (wanted.size === 0) return;
  try {
    const rows = await ctx.storage.getMaterialBatch(ctx.customerId, [...wanted]);
    for (const row of rows) {
      if (row?.ID) state.m_libraryMaterialsById.set(row.ID, row);
    }
  } catch (err) {
    // Non-fatal: the prompt still appears, its material count is just absent.
    console.warn("[ProjectModel] library sync: library material prefetch failed:", err);
  }
}

// -- Apply ------------------------------------------------------------------

async function applyToPlacement(
  model: ProjectModel,
  ctx: StorageContext,
  placement: ModelSyncPlacement,
  partById: ReadonlyMap<string, PartDto>,
  libraryMaterials: ReadonlyMap<string, MaterialDto>,
  asset: ModelSyncAsset,
  excluded: LibrarySyncExclusions,
  signal?: AbortSignal,
): Promise<void> {
  const entity = model.getEntityById(placement.sceneId, placement.entityId);
  if (!entity) return; // deleted while the prompt was up

  const updates: Array<{ snapshotId: string; patch: EntitySnapshotPatch }> = [];

  for (const snapshot of walkSnapshots(entity.snapshots)) {
    const partId = snapshot.partId;
    const part = partId ? partById.get(partId) : undefined;
    if (!part || !partId) continue; // a part the library removed — see the header

    // Whatever the review sheet left unticked is simply not applied. Resolved
    // per PART, the same unit the sheet offers (`libraryDiffKey`).
    const takes = (group: LibraryDiffGroup): boolean =>
      !excluded.has(libraryDiffKey(partId, group));

    // 1. Material VALUES onto the ids this placement already owns. Never the
    //    library's ids: those rows are shared with every other project.
    if (takes("materials")) {
      await pullMaterialValues(model, snapshot, part, libraryMaterials);
    }

    // 2. Snapshot fields the bake writes.
    const patch: Mutable<EntitySnapshotPatch> = {};
    if (takes("other")) {
      const name = typeof part.Name === "string" ? part.Name : undefined;
      const displayName = typeof part.DisplayName === "string" ? part.DisplayName : undefined;
      if (name !== undefined && name !== snapshot.name) patch.name = name;
      if (displayName !== undefined && displayName !== snapshot.displayName) {
        patch.displayName = displayName;
      }
      const visibility = part.Visibility !== false;
      if (visibility !== snapshot.visibility) patch.visibility = visibility;

      // The ROOT's transform is where the user dropped this model in THIS
      // scene. Save to Library deliberately never bakes it, so a pull must
      // never adopt it — doing so teleports every placement to the origin.
      if (!snapshot.isRoot) {
        const transform = libraryTransform(part);
        if (transform && !sameTransform(transform, snapshot.transform)) {
          patch.transform = transform;
        }
      }
    }

    // Aliases MERGE — a replace would delete a language only this project has.
    if (takes("aliases")) {
      const libraryAliases = readAliases(part.Aliases);
      if (libraryAliases.length > 0) {
        const merged = mergeLibraryAliases(snapshot.aliases, libraryAliases);
        if (!sameAliases(merged, snapshot.aliases)) patch.aliases = merged;
      }
    }

    if (Object.keys(patch).length > 0) {
      updates.push({ snapshotId: snapshot.id, patch });
    }
  }

  // 3. Content first, and ALONE. `updateEntitySnapshotsAsync` persists its rows
  //    CONCURRENTLY, so it has no internal ordering — a watermark riding this
  //    batch would land even when a content row failed, and `synced >=
  //    libraryVersion` would then skip this placement forever: never offered
  //    again, the cloud left on the stale values. Throwing here leaves NO
  //    watermark, so the next open re-offers and the pull re-runs; that is
  //    safe because the patches above are recomputed against live state, so a
  //    row that did land produces no patch the second time.
  await model.updateEntitySnapshotsAsync(placement.sceneId, updates, signal);

  // 4. Only now is "this placement is at libraryVersion" a true claim. Keep it
  //    a separate, LATER write — do not fold it back into (3).
  //
  //    Stamped even when the user unticked part of the pull. The watermark
  //    means "this library state has been REVIEWED", not "identical to it" —
  //    they were shown this version and decided. Withholding it on a partial
  //    accept would re-offer the same version at every project open, which is
  //    nagging someone about a choice they already made.
  await model.updateEntitySnapshotsAsync(
    placement.sceneId,
    [{
      snapshotId: placement.rootSnapshotId,
      patch: { librarySyncedVersion: asset.libraryVersion },
    }],
    signal,
  );
  void ctx;
}

/**
 * Copy each library material's VALUES onto the id this placement owns, slot by
 * slot.
 *
 * Length must match exactly: a merged part carries its subtree's materials as
 * one ordered list, and padding or truncating mis-assigns renderers. A
 * mismatch skips the whole part, matching the scene reset's rule.
 */
async function pullMaterialValues(
  model: ProjectModel,
  snapshot: EntitySnapshot,
  part: PartDto,
  libraryMaterials: ReadonlyMap<string, MaterialDto>,
): Promise<void> {
  const libIds = partMaterialIds(part);
  if (libIds.length === 0) return;
  if (libIds.length !== snapshot.materialIds.length) return;

  for (let i = 0; i < libIds.length; i++) {
    const source = libraryMaterials.get(libIds[i]);
    if (!source) continue; // unreadable library row — leave this slot alone
    let ownId = snapshot.materialIds[i];
    if (!ownId) continue;
    // A placement made before per-placement copies existed (or after a Reset
    // Scene) still names the library's row. Writing there would edit the
    // library itself, which is the opposite of what this pull is for.
    if (model.isLibraryOwnedMaterial(ownId)) {
      ownId = (await model.forkLibraryMaterialAsync(ownId)) ?? ownId;
      if (model.isLibraryOwnedMaterial(ownId)) continue; // fork refused — skip
    }
    await model.updateMaterialDataAsync({ ...source, ID: ownId });
  }
}

async function readLibraryMaterials(
  ctx: StorageContext,
  parts: ReadonlyArray<PartDto>,
  signal?: AbortSignal,
): Promise<ReadonlyMap<string, MaterialDto>> {
  const ids = [...new Set(parts.flatMap((p) => partMaterialIds(p)).filter(Boolean))];
  const out = new Map<string, MaterialDto>();
  if (ids.length === 0) return out;
  try {
    const rows = await ctx.storage.getMaterialBatch(ctx.customerId, ids, signal);
    for (const row of rows) {
      if (row?.ID) out.set(row.ID, row);
    }
  } catch (err) {
    // Non-fatal: the rest of the pull still runs. Materials are cosmetic.
    console.warn("[ProjectModel] library sync: getMaterialBatch failed:", err);
  }
  return out;
}

// -- Back-fill --------------------------------------------------------------

async function backfillWatermarksAsync(
  model: ProjectModel,
  sceneId: string,
): Promise<void> {
  const state = internals(model);
  if (!state.getStorageContext()) return;

  const updates: Array<{ snapshotId: string; patch: { librarySyncedVersion: number } }> = [];
  for (const entity of model.getEntities(sceneId)) {
    const assetId = entity.assetId;
    if (!assetId) continue;
    const version = state.m_assetVersionById.get(assetId);
    if (typeof version !== "number" || version <= 0) continue;
    const root = rootSnapshotOf(entity);
    if (!root || typeof root.librarySyncedVersion === "number") continue;
    updates.push({ snapshotId: root.id, patch: { librarySyncedVersion: version } });
  }
  if (updates.length === 0) return;
  await model.updateEntitySnapshotsAsync(sceneId, updates);
}

// -- Helpers ----------------------------------------------------------------

function rootSnapshotOf(entity: EntityData): EntitySnapshot | null {
  for (const snapshot of walkSnapshots(entity.snapshots)) {
    if (snapshot.isRoot) return snapshot;
  }
  return entity.snapshots[0] ?? null;
}

function* walkSnapshots(
  snapshots: ReadonlyArray<EntitySnapshot>,
): Generator<EntitySnapshot> {
  for (const snapshot of snapshots) {
    yield snapshot;
    yield* walkSnapshots(snapshot.children);
  }
}

/**
 * A part row's authored transform, with the zero-scale guard.
 *
 * `vecAttribute` defaults an ABSENT vector to `{0,0,0}`, and rows with no
 * Scale exist (`placeLibraryAssetAsync` asserts as much). Adopting that
 * faithfully would collapse the part to nothing in every project that accepts.
 */
function libraryTransform(part: PartDto): EntitySnapshot["transform"] | null {
  // `partTransform` returns Unity `{x,y,z}` maps; `vec3From` is the same
  // converter `mapEntities` uses to build a snapshot's transform, so the two
  // sides of the comparison are in one space.
  const t = partTransform(part);
  const scale = vec3From(t.scale, [1, 1, 1]);
  return {
    position: vec3From(t.position, [0, 0, 0]),
    rotation: vec3From(t.rotation, [0, 0, 0]),
    // An ABSENT Scale marshals to `{0,0,0}` (`vecAttribute`'s default) and
    // such rows exist — `placeLibraryAssetAsync` guards the same case. Adopting
    // it faithfully would collapse the part to nothing in every project that
    // accepted the update.
    scale: scale[0] === 0 && scale[1] === 0 && scale[2] === 0 ? [1, 1, 1] : scale,
  };
}

function sameTransform(
  a: EntitySnapshot["transform"],
  b: EntitySnapshot["transform"],
): boolean {
  const near = (x: number, y: number): boolean => Math.abs(x - y) < 1e-5;
  return (
    a.position.every((v, i) => near(v, b.position[i]))
    && a.rotation.every((v, i) => near(v, b.rotation[i]))
    && a.scale.every((v, i) => near(v, b.scale[i]))
  );
}

function sameAliases(
  a: ReadonlyArray<PartAlias>,
  b: ReadonlyArray<PartAlias>,
): boolean {
  if (a.length !== b.length) return false;
  const key = (x: PartAlias): string => `${x.language}${x.value}`;
  const set = new Set(b.map(key));
  return a.every((x) => set.has(key(x)));
}

interface StorageContext {
  storage: IStorageModel;
  customerId: string;
  projectId: string;
}

interface SyncInternals {
  readonly m_entitiesByScene: Map<string, EntityData[]>;
  readonly m_assetVersionById: Map<string, number>;
  readonly m_partBakeById: Map<
    string,
    { readonly name: string; readonly displayName: string; readonly visibility: boolean }
  >;
  readonly m_partTransformById: Map<string, EntitySnapshot["transform"]>;
  readonly m_partMaterialIdsById: Map<string, ReadonlyArray<string>>;
  readonly m_partAliasesById: Map<string, ReadonlyArray<PartAlias>>;
  readonly m_materialsById: Map<string, MaterialDto>;
  readonly m_libraryMaterialsById: Map<string, MaterialDto>;
  getStorageContext(): StorageContext | null;
}

function internals(model: ProjectModel): SyncInternals {
  return model as unknown as SyncInternals;
}
